
/**
 * Module dependencies.
 */

import { QueryObserverBaseResult, useQuery } from 'react-query';
import { fetchCollection } from './fetch-collection';
import { useMemo } from 'react';
import { useRouter } from 'next/router';
import get from 'lodash/get';

/**
 * `Props` type.
 */

type Props<P> = QueryObserverBaseResult<P, unknown>;

/**
 * `Options` type.
 */

type Options<P> = {
  initialData?: P | null,
  slug: string
};

/**
 * Action type.
 */

const actionType = ({ locale, slug }): string => {
  return `${locale}-collection-${slug}`;
};

/**
 * `useCollection` hook.
 */

function useCollection<P>(options: Options<P>): Props<P> {
  const { locale, query } = useRouter();
  const variables = useMemo(() => ({
    hasPreview: get(query, 'preview'),
    locale,
    slug: options?.slug
  }), [locale, options.slug, query]);

  const result = useQuery([actionType(variables), variables], () => {
    return fetchCollection(variables);
  }, {
    initialData: options?.initialData
  });

  return result;
}

/**
 * Export `useCollection` hook.
 */

export default useCollection;
